// src/components/NewsFeed.js
import React, { useState, useEffect } from 'react';
import { 
  Newspaper, 
  ExternalLink, 
  Calendar,
  TrendingUp,
  AlertTriangle,
  Clock
} from 'lucide-react';

const NewsFeed = ({ symbols, darkMode }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('ALL');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNews();
  }, [selectedSymbol, filter]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      // Simulate API call to news service
      const mockNews = generateMockNews(symbols, selectedSymbol, filter);
      setNews(mockNews);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.floor((new Date(date) - new Date()) / (1000 * 60)),
      'minute'
    );
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return darkMode ? 'text-green-400' : 'text-green-600';
      case 'negative': return darkMode ? 'text-red-400' : 'text-red-600';
      default: return darkMode ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <TrendingUp size={14} />;
      case 'negative': return <AlertTriangle size={14} />;
      default: return <Newspaper size={14} />;
    }
  };

  const handleButtonClick = async (action, value, event) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      switch (action) {
        case 'changeSymbolFilter':
          setSelectedSymbol(value);
          break;
        case 'changeNewsFilter':
          setFilter(value);
          break;
        case 'refreshNews':
          await fetchNews();
          break;
        default:
          console.warn('Unknown news action:', action);
      }
    } catch (error) {
      console.error('Error in news button click:', error);
    }
  };  

  if (loading) {
    return (
      <div className={`rounded-lg shadow-lg p-6 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded mb-3"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-lg p-6 ${
      darkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center">
          <Newspaper size={24} className="mr-3 text-blue-600" />
          <h3 className="text-2xl font-bold">Market News</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Symbol Filter */}
          <select
            value={selectedSymbol}
            onChange={(e) => handleButtonClick('changeSymbolFilter', e.target.value, e)}
            className={`px-3 py-2 rounded border text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="ALL">All Stocks</option>
            {symbols.map(symbol => (
              <option key={symbol} value={symbol}>{symbol}</option>
            ))}
          </select>

          {/* News Type Filter */}
          <select
            value={filter}
            onChange={(e) => handleButtonClick('changeNewsFilter', e.target.value, e)}
            className={`px-3 py-2 rounded border text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">All News</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="breaking">Breaking</option>
          </select>
        </div>
      </div>

      {/* News List */}
      <div className="space-y-4">
        {news.map((item, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border transition-all hover:shadow-md ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 hover:border-gray-500' 
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  darkMode ? 'bg-gray-600' : 'bg-gray-100'
                }`}>
                  {item.symbol}
                </span>
                <span className={`flex items-center text-xs ${getSentimentColor(item.sentiment)}`}>
                  {getSentimentIcon(item.sentiment)}
                  <span className="ml-1">{item.sentiment}</span>
                </span>
              </div>
              
              <div className="flex items-center text-xs text-gray-500">
                <Clock size={12} className="mr-1" />
                {formatTime(item.timestamp)}
              </div>
            </div>

            <h4 className={`font-semibold mb-2 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {item.title}
            </h4>

            <p className={`text-sm mb-3 ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {item.summary}
            </p>

            <div className="flex items-center justify-between">
              <span className={`text-xs ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Source: {item.source}
              </span>
              
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center text-xs ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                }`}
              >
                Read more
                <ExternalLink size={12} className="ml-1" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {news.length === 0 && (
        <div className={`text-center py-12 ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
          <p>No news available for the selected filters</p>
        </div>
      )}
    </div>
  );
};

// Mock news generator
const generateMockNews = (symbols, selectedSymbol, filter) => {
  const sources = ['Bloomberg', 'Reuters', 'CNBC', 'Wall Street Journal', 'Financial Times'];
  const sentiments = ['positive', 'negative', 'neutral'];
  const newsTypes = ['earnings', 'analysis', 'market', 'breaking', 'regulatory'];
  
  const filteredSymbols = selectedSymbol === 'ALL' ? symbols : [selectedSymbol];
  
  return filteredSymbols.flatMap(symbol => {
    const newsCount = Math.floor(Math.random() * 3) + 1;
    return Array.from({ length: newsCount }, (_, i) => {
      const sentiment = sentiments[Math.floor(Math.random() * sentiments.length)];
      const type = newsTypes[Math.floor(Math.random() * newsTypes.length)];
      
      // Apply filter
      if (filter !== 'all' && filter !== sentiment && filter !== type) {
        return null;
      }

      return {
        symbol,
        title: `${symbol} ${getNewsTitle(type)}`,
        summary: `This is a sample news summary about ${symbol}. The stock has been ${sentiment} recently due to market conditions.`,
        url: '#',
        source: sources[Math.floor(Math.random() * sources.length)],
        sentiment,
        type,
        timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString()
      };
    }).filter(Boolean);
  }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);
};

const getNewsTitle = (type) => {
  const titles = {
    earnings: 'Q4 Earnings Beat Expectations',
    analysis: 'Analysts Upgrade Price Target',
    market: 'Market Performance Update',
    breaking: 'Breaking News: Major Announcement',
    regulatory: 'Regulatory Approval Received'
  };
  return titles[type] || 'Market News Update';
};

export default NewsFeed;